import React, { useState } from 'react';
import cs from 'classnames';
import shortid from 'shortid';

import './Quantity.scss';
import { Input } from 'reactstrap';
import { useDebouncedCallback } from 'use-debounce';

interface Props {
  loading?: boolean;
  disabled?: boolean;
  disableDecrement?: boolean;
  disableIncrement?: boolean;
  hideMobile?: boolean;
  maxQuantity?: number;
  quantity: number;
  onChange: (value: number) => void;
}

export const Quantity = (props: Props) => {
  const [value, setValue] = useState<number>(props.quantity || 1);
  const maxQty =
    typeof props.maxQuantity === 'number' && props.maxQuantity > 0
      ? props.maxQuantity
      : undefined;

  const debounced = useDebouncedCallback((val) => {
    if (!val || val < 0) {
      setValue(1);
      props.onChange(1);
      return;
    }
    props.onChange(val);
  }, 500);

  const stopCarouselDrag = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const decrement = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const newQty = value - 1;
    const newValue = newQty <= 0 ? 1 : newQty;

    setValue(newValue);
    props.onChange(newValue);
  };

  const increment = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const newQty = value + 1;
    const newValue = maxQty && newQty > maxQty ? maxQty : newQty;

    setValue(newValue);
    props.onChange(newValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = parseInt(e.target.value, 10);
    if (Number.isNaN(nextValue) || nextValue < 1) {
      setValue(1);
      debounced(1);
      return;
    }
    if (maxQty && nextValue > maxQty) {
      setValue(maxQty);
      debounced(maxQty);
      return;
    }
    setValue(nextValue);
    debounced(nextValue);
  };

  const isIncrementDisabled =
    props.disabled ||
    props.disableIncrement ||
    (typeof maxQty === 'number' && value >= maxQty);

  return (
    <div
      className={cs('Quantity', {
        // "Quantity--hide_mobile": props.hideMobile
      })}
      onMouseDown={stopCarouselDrag}
      onPointerDown={stopCarouselDrag}
      onTouchStart={stopCarouselDrag}
      onClick={stopCarouselDrag}
    >
      <div className="Quantity__input">
        <button
          type="button"
          className="minus"
          disabled={props.disabled || props.disableDecrement || value <= 1}
          onMouseDown={stopCarouselDrag}
          onClick={decrement}
        >
          -
        </button>
        <Input
          type="number"
          name="quantity"
          min={1}
          max={maxQty}
          value={value}
          onChange={handleChange}
          onMouseDown={stopCarouselDrag}
          className="form-control Quantity__input__field"
        />
        <button
          type="button"
          className="plus"
          onMouseDown={stopCarouselDrag}
          onClick={increment}
          disabled={isIncrementDisabled}
        >
          +
        </button>
      </div>
    </div>
  );
};

interface QuantitySelectProps {
  disabled?: boolean;
  max?: number;
  value: number;
  onChange: (quantity: number) => void;
}

export const QuantitySelect = (props: QuantitySelectProps) => {
  const options = props.max
    ? Array.from({ length: props.max }, (v, i) => {
        return { key: shortid.generate(), value: i + 1 };
      })
    : null;

  const handleChange = (newValue: number) => {
    props.onChange(newValue);
  };

  return (
    <label>
      <select
        disabled={props.disabled || !options}
        id="qty"
        name="qty"
        value={props.value}
        onChange={(e) => handleChange(parseInt(e.target.value))}
      >
        {options?.map((item) => (
          <option value={item.value} key={`${item.key}-quantity`}>
            {item.value}
          </option>
        ))}
      </select>
    </label>
  );
};
